import type { Element, ElementContent, Root as HastRoot } from 'hast'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'
import { DOCUMENT_TYPE_LABELS } from '../dialect.ts'
import { formatIsoDate } from '../format.ts'
import type {
  ClinicLogo,
  Evidence,
  EvidenceSigner,
  ParsedDocument,
  RenderMode,
  RenderOptions,
} from '../types.ts'

/**
 * mdast (já transformado por remarkLegal) → hast completo do documento:
 * cabeçalho · corpo · bloco de prova · rodapé de marca.
 * É a árvore que vira React (tela) ou string HTML (e-mail, PDF).
 */

function h(
  tagName: string,
  className: string | string[],
  children: ElementContent[] = [],
  extra: Record<string, unknown> = {},
): Element {
  const classes = (Array.isArray(className) ? className : [className]).filter(Boolean)
  return {
    type: 'element',
    tagName,
    properties: { ...(classes.length ? { className: classes } : {}), ...extra },
    children,
  }
}

function t(value: string): ElementContent {
  return { type: 'text', value }
}

/** PNG ou JPEG embutido em base64: o único logo que o documento aceita (o HTML é autocontido). */
export const LOGO_SOURCE_PATTERN = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/

/** Onde o logo entra (P-DOC-06): no cabeçalho, como marca d'água (só na impressão) ou em lugar nenhum. */
export function logoPlacement(
  logo: ClinicLogo | null | undefined,
  mode: RenderMode = 'screen',
): 'header' | 'watermark' | null {
  if (!logo || !LOGO_SOURCE_PATTERN.test(logo.src)) return null
  if (logo.position === 'watermark') return mode === 'print' ? 'watermark' : null
  return 'header'
}

function logoImage(src: string, alt: string): Element {
  return { type: 'element', tagName: 'img', properties: { src, alt }, children: [] }
}

/**
 * O cabeçalho do documento: tipo, título e a linha de procedimento, versão e data. Com `logo`, a imagem vem
 * antes do tipo, alinhada à esquerda (P-DOC-06).
 */
export function headerHast(doc: ParsedDocument, logo?: { src: string; alt: string }): Element {
  const { meta } = doc
  const lines: string[] = []
  if (meta.procedure) lines.push(meta.procedure)
  if (meta.version) lines.push(`Versão ${meta.version}`)
  if (meta.updatedAt) lines.push(`atualizado em ${formatIsoDate(meta.updatedAt)}`)
  const children: ElementContent[] = []
  if (logo) children.push(h('div', 'wx-header-logo', [logoImage(logo.src, logo.alt)]))
  children.push(h('div', 'wx-header-type', [t(DOCUMENT_TYPE_LABELS[meta.type])]))
  if (meta.title) children.push(h('h1', 'wx-header-title', [t(meta.title)]))
  if (lines.length) children.push(h('div', 'wx-header-meta', [t(lines.join(' · '))]))
  return h('header', 'wx-header', children)
}

export function evidenceHast(evidence: Evidence): Element {
  const children: ElementContent[] = [h('div', 'wx-evidence-title', [t('Registro')])]
  const lines: ElementContent[] = []
  if (evidence.statement) {
    lines.push(t(evidence.statement), {
      type: 'element',
      tagName: 'br',
      properties: {},
      children: [],
    })
  } else if (evidence.signedAt) {
    const d = new Date(evidence.signedAt)
    const date = Number.isNaN(d.getTime())
      ? evidence.signedAt
      : `${formatIsoDate(d.toISOString())} · ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} UTC`
    lines.push(t('Assinado em '), h('b', [], [t(date)]))
    if (evidence.signers?.length) lines.push(t(` por ${evidence.signers.join(', ')}`))
    lines.push(t('.'), { type: 'element', tagName: 'br', properties: {}, children: [] })
  }
  lines.push(t('Protocolo '), h('b', [], [t(evidence.protocolNumber)]))
  if (evidence.device) lines.push(t(` · ${evidence.device}`))
  if (evidence.ip) lines.push(t(` · IP ${evidence.ip}`))
  lines.push({ type: 'element', tagName: 'br', properties: {}, children: [] })
  lines.push(t('Hash do documento '), h('span', 'wx-evidence-hash', [t(evidence.hash)]))
  children.push(h('p', [], lines))
  return h('footer', 'wx-evidence', children, {
    'data-protocol-number': evidence.protocolNumber,
  })
}

function evidenceLine(label: string, value: string | null | undefined): ElementContent | null {
  if (!value) return null
  return h('div', 'wx-evidence-line', [
    h('span', 'wx-evidence-label', [t(label)]),
    t(' '),
    h('span', [], [t(value)]),
  ])
}

function signerHast(s: EvidenceSigner): Element {
  const lines = [
    evidenceLine('Nome', s.name),
    evidenceLine('Documento', s.docNumber),
    evidenceLine('Assinado em', [s.signedAtLocal, s.signedAtUtc].filter(Boolean).join(' · ')),
    evidenceLine('Como', s.how),
    evidenceLine('IP', s.ip),
    evidenceLine('Dispositivo', s.device),
    evidenceLine('Aceite', s.acceptanceText ? `“${s.acceptanceText}”` : null),
  ].filter((x): x is ElementContent => x !== null)
  const children: ElementContent[] = [h('div', 'wx-evidence-signer-role', [t(s.role)]), ...lines]
  if (s.drawnSignatureDataUrl) {
    children.push(
      h('div', 'wx-evidence-drawn', [
        {
          type: 'element',
          tagName: 'img',
          properties: { src: s.drawnSignatureDataUrl, alt: `Traço de ${s.name}` },
          children: [],
        },
      ]),
    )
  }
  if (s.recordHash) {
    children.push(
      h('div', 'wx-evidence-line', [
        h('span', 'wx-evidence-label', [t('Hash do registro')]),
        t(' '),
        h('span', 'wx-evidence-hash', [t(s.recordHash)]),
      ]),
    )
  }
  return h('section', 'wx-evidence-signer', children)
}

/**
 * A página de evidências anexa ao PDF assinado (P-ASS-06, RN-ASS-27): protocolo, documento, hash, cada
 * signatário com nome, papel, documento, data e hora nos dois fusos, IP, dispositivo, traço e aceite, o
 * hash do registro e a versão do render. Vem depois de uma quebra de página.
 */
export function evidencePageHast(evidence: Evidence): Element {
  const page = evidence.page!
  const head: ElementContent[] = [
    h('div', 'wx-evidence-page-eyebrow', [t('Registro de assinatura eletrônica')]),
    h('h2', 'wx-evidence-page-title', [t(`Protocolo ${evidence.protocolNumber}`)]),
  ]
  const facts = [
    evidenceLine(
      'Documento',
      [page.documentTitle, page.templateVersion ? `versão ${page.templateVersion}` : null]
        .filter(Boolean)
        .join(' · '),
    ),
    evidenceLine('Clínica', page.clinicName),
    h('div', 'wx-evidence-line', [
      h('span', 'wx-evidence-label', [t('Hash do documento')]),
      t(' '),
      h('span', 'wx-evidence-hash', [t(evidence.hash)]),
    ]),
  ].filter((x): x is ElementContent => x !== null)
  const signers = page.signatures.map(signerHast)
  const foot = h('div', 'wx-evidence-page-foot', [
    t(
      `Registro produzido pela Westhetics (render ${page.renderVersion}${
        page.generatedAt ? `, em ${page.generatedAt}` : ''
      }). O hash do documento é o SHA-256 do texto exatamente como foi lido; o hash do registro cobre nome, documento, data, hora, IP, dispositivo e aceite de cada assinatura. A beleza deve ser segura.`,
    ),
  ])
  return h('section', 'wx-evidence-page', [
    ...head,
    h('div', 'wx-evidence-facts', facts),
    ...signers,
    foot,
  ])
}

export function documentHast(doc: ParsedDocument, options: RenderOptions = {}): HastRoot {
  const { scale = 'normal', mode = 'screen', showHeader = true, clinicName } = options

  const body = unified()
    .use(remarkRehype, { allowDangerousHtml: false })
    .runSync(doc.tree) as HastRoot

  const placement = logoPlacement(options.logo, mode)
  const logoAlt = clinicName ? `Logo de ${clinicName}` : 'Logo da clínica'

  const children: ElementContent[] = []
  // A marca d'água fica atrás do texto em todas as páginas impressas; é decoração, não conteúdo.
  if (placement === 'watermark') {
    children.push(
      h('div', 'wx-watermark', [logoImage(options.logo!.src, '')], { 'aria-hidden': 'true' }),
    )
  }
  if (showHeader) {
    const headerLogo = placement === 'header' ? { src: options.logo!.src, alt: logoAlt } : undefined
    children.push(headerHast(doc, headerLogo))
  }
  children.push(h('div', 'wx-body', body.children as ElementContent[]))
  if (options.evidence) children.push(evidenceHast(options.evidence))
  children.push(
    h('div', 'wx-brand-footer', [
      t(
        `${clinicName ? `${clinicName} · ` : ''}Documento emitido pela Westhetics · A beleza deve ser segura.`,
      ),
    ]),
  )
  if (options.evidence?.page && mode === 'print') {
    children.push(h('div', 'wx-page-break'))
    children.push(evidencePageHast(options.evidence))
  }

  const root = h(
    'article',
    [
      'wx-legal',
      scale === 'large' ? 'wx-legal--large' : '',
      mode === 'print' ? 'wx-legal--print' : '',
    ].filter(Boolean),
    children,
    { lang: options.lang ?? 'pt-BR', 'data-type': doc.meta.type },
  )

  return { type: 'root', children: [root] }
}
